import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Braces,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  MailOpen,
  Paperclip,
  Search,
  ShieldCheck,
  Sparkles,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

type InboxSource = 'email' | 'wms-intake';

interface EmailRow {
  id: string;
  fromAddr?: string | null;
  from_addr?: string | null;
  subject?: string | null;
  direction?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
}

interface EmailRecord {
  id: string;
  tenant_id: string;
  job_id?: string | null;
  message_id?: string | null;
  from_addr: string;
  to_addrs?: string[] | string | null;
  subject?: string | null;
  body_text?: string | null;
  direction?: 'inbound' | 'outbound' | string;
  parsed_data?: Record<string, unknown> | null;
  created_at: string;
  attachments?: Array<{ name: string; type?: string; size?: number; url?: string }> | null;
}

interface WmsInboxRowResponse {
  messageId: string;
  messageRowId?: string | null;
  subject?: string | null;
  from?: string | null;
  receivedAt?: string | null;
  hasAttachments?: boolean;
  intakeId?: string | null;
  intakeStatus?: string | null;
  warehouseName?: string | null;
  linkedJobId?: string | null;
  linkedJobNumber?: string | null;
  linkedJobStatus?: string | null;
  bodyPreview?: string | null;
}

interface WmsIntakeExtraction {
  id: string;
  field_name?: string;
  field_value?: unknown;
  corrected_value?: unknown;
  confidence_score?: number | null;
}

interface WmsIntakeDetail {
  intakeId: string;
  status: string;
  header?: {
    jobType?: string | null;
    direction?: string | null;
    requestDate?: string | null;
    warehouseName?: string | null;
    referenceNo?: string | null;
  } | null;
  linkedJob?: {
    id: string;
    jobNumber?: string | null;
    jobType?: string | null;
    status?: string | null;
  } | null;
  documents?: Array<{
    id: string;
    attachmentId?: string | null;
    docKind?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
    fileSizeBytes?: number | null;
    parseStatus?: string | null;
  }>;
  extractions?: WmsIntakeExtraction[];
  email?: {
    subject?: string | null;
    from?: string | null;
    body?: string | null;
  } | null;
}

interface InboxRow {
  id: string;
  source: InboxSource;
  emailId?: string | null;
  messageId?: string | null;
  messageRowId?: string | null;
  fromAddr?: string | null;
  subject?: string | null;
  direction?: string | null;
  createdAt?: string | null;
  hasAttachments?: boolean;
  intakeId?: string | null;
  intakeStatus?: string | null;
  warehouseName?: string | null;
  linkedJobId?: string | null;
  linkedJobNumber?: string | null;
  linkedJobStatus?: string | null;
  bodyPreview?: string | null;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

interface EmailInboxWorkspaceProps {
  mode?: InboxSource;
  embedded?: boolean;
  defaultWmsStatusFilter?: string[];
}

const PAGE_SIZE = 50;
const DEFAULT_REVIEWER_ROLE = 'warehouse_supervisor';
const EMPTY_WMS_STATUS_FILTER: string[] = [];
const WMS_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'received', label: 'Received' },
  { value: 'parsed', label: 'Parsed' },
  { value: 'review_required', label: 'Review Required' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
] as const;
const DEFAULT_MOCK_INBOX_MODE = true;

interface MockWmsStateItem {
  row: WmsInboxRowResponse;
  detail: WmsIntakeDetail | null;
}

type MockTenantKey = 'new-revo' | 'new-asia';

const NEW_REVO_TENANT_ID = '00000000-0000-4000-a000-000000000053';
const NEW_ASIA_TENANT_ID = '5bcb2460-6334-47d9-8af0-4edbb0b60cbf';
const DEFAULT_MOCK_TENANT_KEY: MockTenantKey = 'new-revo';

const MOCK_EMAIL_RECORDS_BY_TENANT: Record<MockTenantKey, EmailRecord[]> = {
  'new-revo': [
    {
      id: 'mock-revo-email-1',
      tenant_id: NEW_REVO_TENANT_ID,
      job_id: 'job-mock-1001',
      message_id: 'msg-mock-1001',
      from_addr: 'Olivia Tan <olivia.tan@revo-demo.com>',
      to_addrs: ['ops@boox-demo.com', 'warehouse@boox-demo.com'],
      subject: 'Arrival notice for 3 pallets - Monday receiving',
      body_text: 'Hi team,\n\nPlease prepare receiving slot for 3 pallets arriving on Monday at 09:30.\nPO: REV-PO-24001\nSKU group: cosmetics mixed carton\n\nThanks,\nOlivia',
      direction: 'inbound',
      parsed_data: {
        intent: 'warehouse_receiving',
        priority: 'normal',
        sample: true,
        poNumber: 'REV-PO-24001',
      },
      created_at: '2026-04-22T09:30:00.000Z',
      attachments: [
        { name: 'arrival-notice.pdf', type: 'application/pdf', size: 238400, url: '#' },
        { name: 'packing-list.xlsx', type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', size: 84210, url: '#' },
      ],
    },
    {
      id: 'mock-revo-email-2',
      tenant_id: NEW_REVO_TENANT_ID,
      job_id: null,
      message_id: 'msg-mock-1002',
      from_addr: 'Marcus Lee <marcus.lee@forwarder-demo.com>',
      to_addrs: ['ops@boox-demo.com'],
      subject: 'Container ETA update - SGSIN / CMAU1288123',
      body_text: 'Hello,\n\nContainer CMAU1288123 revised ETA is now 24 Apr 18:00 due to port congestion.\nPlease update warehouse booking accordingly.\n\nRegards,\nMarcus',
      direction: 'inbound',
      parsed_data: {
        intent: 'shipment_update',
        priority: 'high',
        sample: true,
        containerNo: 'CMAU1288123',
      },
      created_at: '2026-04-23T02:15:00.000Z',
      attachments: [],
    },
    {
      id: 'mock-revo-email-3',
      tenant_id: NEW_REVO_TENANT_ID,
      job_id: 'job-mock-1003',
      message_id: 'msg-mock-1003',
      from_addr: 'Boox Ops <ops@boox-demo.com>',
      to_addrs: ['customer@example.com'],
      subject: 'Outbound release confirmed',
      body_text: 'Dear customer,\n\nYour outbound order has been released to trucking. Driver ETA at site is 14:30.\n\nBest regards,\nBoox Operations',
      direction: 'outbound',
      parsed_data: {
        intent: 'customer_update',
        priority: 'normal',
        sample: true,
      },
      created_at: '2026-04-23T05:45:00.000Z',
      attachments: [{ name: 'delivery-order.pdf', type: 'application/pdf', size: 145920, url: '#' }],
    },
  ],
  'new-asia': [
    {
      id: 'mock-asia-email-1',
      tenant_id: NEW_ASIA_TENANT_ID,
      job_id: 'job-mock-asia-1001',
      message_id: 'msg-mock-asia-1001',
      from_addr: 'Nur Aisyah <aisyah@newasia-demo.com>',
      to_addrs: ['ops@newasia-demo.com', 'pk.warehouse@boox-demo.com'],
      subject: 'Port Klang inbound booking for 4 pallets',
      body_text: 'Hi team,\n\nPlease prepare receiving slot for 4 pallets arriving at Port Klang warehouse tomorrow morning.\nReference: NAS-PO-24018\nSpecial note: mixed beauty and household SKU.\n\nThanks,\nAisyah',
      direction: 'inbound',
      parsed_data: {
        intent: 'warehouse_receiving',
        priority: 'high',
        sample: true,
        poNumber: 'NAS-PO-24018',
      },
      created_at: '2026-04-23T01:05:00.000Z',
      attachments: [{ name: 'port-klang-booking.pdf', type: 'application/pdf', size: 214220, url: '#' }],
    },
    {
      id: 'mock-asia-email-2',
      tenant_id: NEW_ASIA_TENANT_ID,
      job_id: null,
      message_id: 'msg-mock-asia-1002',
      from_addr: 'Farid Logistics <farid@linehaul-demo.com>',
      to_addrs: ['ops@newasia-demo.com'],
      subject: 'Urgent transfer request to Johor crossdock',
      body_text: 'Good morning,\n\nNeed urgent transfer for 12 bundles to Johor crossdock before 18:00 today.\nReference: JHR-XD-118.\n\nRegards,\nFarid',
      direction: 'inbound',
      parsed_data: {
        intent: 'warehouse_transfer',
        priority: 'high',
        sample: true,
        transferReference: 'JHR-XD-118',
      },
      created_at: '2026-04-23T03:20:00.000Z',
      attachments: [],
    },
    {
      id: 'mock-asia-email-3',
      tenant_id: NEW_ASIA_TENANT_ID,
      job_id: 'job-mock-asia-1003',
      message_id: 'msg-mock-asia-1003',
      from_addr: 'New Asia Customer Care <care@newasia-demo.com>',
      to_addrs: ['customer@retail-demo.com'],
      subject: 'Outbound delivery to Shah Alam confirmed',
      body_text: 'Dear customer,\n\nYour outbound order has been picked, packed and dispatched to Shah Alam. Driver ETA is 15:45.\n\nBest regards,\nNew Asia Customer Care',
      direction: 'outbound',
      parsed_data: {
        intent: 'customer_update',
        priority: 'normal',
        sample: true,
      },
      created_at: '2026-04-23T06:05:00.000Z',
      attachments: [{ name: 'delivery-order-shah-alam.pdf', type: 'application/pdf', size: 132800, url: '#' }],
    },
  ],
};

function createInitialMockWmsState(tenantKey: MockTenantKey): MockWmsStateItem[] {
  if (tenantKey === 'new-asia') {
    return [
      {
        row: {
          messageId: 'mock-asia-wms-msg-1',
          messageRowId: 'mock-asia-wms-row-1',
          subject: 'Port Klang receiving request - 4 pallets with label check',
          from: 'PK Operations <pk.ops@customer-demo.com>',
          receivedAt: '2026-04-23T00:40:00.000Z',
          hasAttachments: true,
          intakeId: 'mock-asia-intake-1',
          intakeStatus: 'review_required',
          warehouseName: 'Port Klang DC-1',
          linkedJobId: 'job-mock-asia-wms-1',
          linkedJobNumber: 'WMS-MY-240423-001',
          linkedJobStatus: 'draft',
          bodyPreview: 'Please receive 4 pallets at Port Klang DC-1 and verify Chinese, Bahasa Melayu and Japanese labels before putaway.',
        },
        detail: {
          intakeId: 'mock-asia-intake-1',
          status: 'review_required',
          header: {
            jobType: 'WAREHOUSE_IN',
            direction: 'inbound',
            requestDate: '2026-04-24',
            warehouseName: 'Port Klang DC-1',
            referenceNo: 'PK-7781',
          },
          linkedJob: {
            id: 'job-mock-asia-wms-1',
            jobNumber: 'WMS-MY-240423-001',
            jobType: 'WAREHOUSE_IN',
            status: 'draft',
          },
          documents: [
            {
              id: 'mock-asia-doc-1',
              attachmentId: 'att-asia-1',
              docKind: 'PACKING_LIST',
              fileName: 'packing-list-port-klang.pdf',
              mimeType: 'application/pdf',
              fileSizeBytes: 166400,
              parseStatus: 'PARSED',
            },
          ],
          extractions: [
            { id: 'ext-asia-1', field_name: 'warehouse_name', field_value: 'Port Klang DC-1', corrected_value: 'Port Klang DC-1', confidence_score: 0.98 },
            { id: 'ext-asia-2', field_name: 'reference_no', field_value: 'PK-7781', corrected_value: 'PK-7781', confidence_score: 0.97 },
            { id: 'ext-asia-3', field_name: 'pallet_count', field_value: '4', corrected_value: '4', confidence_score: 0.96 },
          ],
          email: {
            subject: 'Port Klang receiving request - 4 pallets with label check',
            from: 'PK Operations <pk.ops@customer-demo.com>',
            body: 'Please receive 4 pallets tomorrow morning. Product labels must be checked in Chinese, Bahasa Melayu and Japanese before storage.',
          },
        },
      },
      {
        row: {
          messageId: 'mock-asia-wms-msg-2',
          messageRowId: 'mock-asia-wms-row-2',
          subject: 'Bundle weighing request for export staging',
          from: 'Export Desk <export@newasia-demo.com>',
          receivedAt: '2026-04-23T04:25:00.000Z',
          hasAttachments: true,
          intakeId: null,
          intakeStatus: 'received',
          warehouseName: 'Port Klang Export Yard',
          linkedJobId: null,
          linkedJobNumber: null,
          linkedJobStatus: null,
          bodyPreview: 'Need bundle-level weighing for 12 bundles before export staging. Reference BDL-EX-991.',
        },
        detail: null,
      },
      {
        row: {
          messageId: 'mock-asia-wms-msg-3',
          messageRowId: 'mock-asia-wms-row-3',
          subject: 'Approved outbound to Shah Alam retail hub',
          from: 'Retail MY <retail.my@customer-demo.com>',
          receivedAt: '2026-04-22T15:55:00.000Z',
          hasAttachments: false,
          intakeId: 'mock-asia-intake-3',
          intakeStatus: 'approved',
          warehouseName: 'Shah Alam Hub',
          linkedJobId: 'job-mock-asia-wms-3',
          linkedJobNumber: 'WMS-MY-240422-009',
          linkedJobStatus: 'pending',
          bodyPreview: 'Retail replenishment approved. Proceed with pick, pack and dispatch to Shah Alam hub tonight.',
        },
        detail: {
          intakeId: 'mock-asia-intake-3',
          status: 'approved',
          header: {
            jobType: 'WAREHOUSE_OUT',
            direction: 'outbound',
            requestDate: '2026-04-22',
            warehouseName: 'Shah Alam Hub',
            referenceNo: 'SA-2209',
          },
          linkedJob: {
            id: 'job-mock-asia-wms-3',
            jobNumber: 'WMS-MY-240422-009',
            jobType: 'WAREHOUSE_OUT',
            status: 'pending',
          },
          documents: [],
          extractions: [
            { id: 'ext-asia-31', field_name: 'direction', field_value: 'outbound', corrected_value: 'outbound', confidence_score: 0.94 },
          ],
          email: {
            subject: 'Approved outbound to Shah Alam retail hub',
            from: 'Retail MY <retail.my@customer-demo.com>',
            body: 'Please proceed with outbound execution and truck release before 21:00.',
          },
        },
      },
    ];
  }
  return [
    {
      row: {
        messageId: 'mock-wms-msg-1',
        messageRowId: 'mock-wms-row-1',
        subject: 'Inbound receiving request - 2 pallets for Jurong WH',
        from: 'Warehouse Planner <planner@customer-demo.com>',
        receivedAt: '2026-04-23T01:20:00.000Z',
        hasAttachments: true,
        intakeId: 'mock-intake-1',
        intakeStatus: 'review_required',
        warehouseName: 'Jurong WH-A',
        linkedJobId: 'job-mock-wms-1',
        linkedJobNumber: 'WMS-SG-240423-001',
        linkedJobStatus: 'draft',
        bodyPreview: 'Please receive 2 pallets of skincare cartons tomorrow morning. Reference REF-7781. Need QC photo capture on arrival.',
      },
      detail: {
        intakeId: 'mock-intake-1',
        status: 'review_required',
        header: {
          jobType: 'WAREHOUSE_IN',
          direction: 'inbound',
          requestDate: '2026-04-24',
          warehouseName: 'Jurong WH-A',
          referenceNo: 'REF-7781',
        },
        linkedJob: {
          id: 'job-mock-wms-1',
          jobNumber: 'WMS-SG-240423-001',
          jobType: 'WAREHOUSE_IN',
          status: 'draft',
        },
        documents: [
          {
            id: 'mock-doc-1',
            attachmentId: 'att-1',
            docKind: 'PACKING_LIST',
            fileName: 'packing-list-receiving.pdf',
            mimeType: 'application/pdf',
            fileSizeBytes: 198200,
            parseStatus: 'PARSED',
          },
          {
            id: 'mock-doc-2',
            attachmentId: 'att-2',
            docKind: 'PURCHASE_ORDER',
            fileName: 'po-ref-7781.pdf',
            mimeType: 'application/pdf',
            fileSizeBytes: 112300,
            parseStatus: 'PARSED',
          },
        ],
        extractions: [
          { id: 'ext-1', field_name: 'warehouse_name', field_value: 'Jurong WH-A', corrected_value: 'Jurong WH-A', confidence_score: 0.98 },
          { id: 'ext-2', field_name: 'reference_no', field_value: 'REF-7781', corrected_value: 'REF-7781', confidence_score: 0.97 },
          { id: 'ext-3', field_name: 'pallet_count', field_value: '2', corrected_value: '2', confidence_score: 0.95 },
        ],
        email: {
          subject: 'Inbound receiving request - 2 pallets for Jurong WH',
          from: 'Warehouse Planner <planner@customer-demo.com>',
          body: 'Hi warehouse team,\n\nPlease receive 2 pallets of skincare cartons tomorrow morning.\nReference: REF-7781\nSpecial handling: take QC photo and check expiry date.\n\nThanks.',
        },
      },
    },
    {
      row: {
        messageId: 'mock-wms-msg-2',
        messageRowId: 'mock-wms-row-2',
        subject: 'Urgent outbound pick request for Port Klang transfer',
        from: 'Asia Ops <asia.ops@customer-demo.com>',
        receivedAt: '2026-04-23T04:10:00.000Z',
        hasAttachments: true,
        intakeId: null,
        intakeStatus: 'received',
        warehouseName: 'Port Klang Overflow',
        linkedJobId: null,
        linkedJobNumber: null,
        linkedJobStatus: null,
        bodyPreview: 'Need urgent pick, pack and transfer to Port Klang by 16:00 today. Reference PK-OUT-991.',
      },
      detail: null,
    },
    {
      row: {
        messageId: 'mock-wms-msg-3',
        messageRowId: 'mock-wms-row-3',
        subject: 'Approved cross-dock request for retail replenishment',
        from: 'Retail Demand <retail@customer-demo.com>',
        receivedAt: '2026-04-22T16:50:00.000Z',
        hasAttachments: false,
        intakeId: 'mock-intake-3',
        intakeStatus: 'approved',
        warehouseName: 'Tuas Crossdock',
        linkedJobId: 'job-mock-wms-3',
        linkedJobNumber: 'WMS-SG-240422-007',
        linkedJobStatus: 'pending',
        bodyPreview: 'Cross-dock replenishment has been approved. Please proceed with wave preparation for tonight dispatch.',
      },
      detail: {
        intakeId: 'mock-intake-3',
        status: 'approved',
        header: {
          jobType: 'WAREHOUSE_OUT',
          direction: 'outbound',
          requestDate: '2026-04-22',
          warehouseName: 'Tuas Crossdock',
          referenceNo: 'RPL-2207',
        },
        linkedJob: {
          id: 'job-mock-wms-3',
          jobNumber: 'WMS-SG-240422-007',
          jobType: 'WAREHOUSE_OUT',
          status: 'pending',
        },
        documents: [],
        extractions: [
          { id: 'ext-31', field_name: 'direction', field_value: 'outbound', corrected_value: 'outbound', confidence_score: 0.94 },
        ],
        email: {
          subject: 'Approved cross-dock request for retail replenishment',
          from: 'Retail Demand <retail@customer-demo.com>',
          body: 'Please proceed with cross-dock and stage all cartons at gate 5 before 21:00.',
        },
      },
    },
  ];
}

let mockWmsStateByTenant: Record<MockTenantKey, MockWmsStateItem[]> = {
  'new-revo': createInitialMockWmsState('new-revo'),
  'new-asia': createInitialMockWmsState('new-asia'),
};

function cloneData<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function matchesKeyword(haystacks: Array<string | null | undefined>, keyword: string): boolean {
  const q = keyword.trim().toLowerCase();
  if (!q) return true;
  return haystacks.some((item) => String(item ?? '').toLowerCase().includes(q));
}

function paginateItems<T>(items: T[], page: number, limit: number): PaginatedResponse<T> {
  const start = (page - 1) * limit;
  return {
    items: items.slice(start, start + limit),
    total: items.length,
    page,
    limit,
  };
}

function getMockTenantKey(context?: {
  tenantId?: string | null;
  tenantName?: string | null;
  userEmail?: string | null;
}): MockTenantKey {
  const tenantId = String(context?.tenantId ?? '').trim();
  const tenantName = String(context?.tenantName ?? '').trim().toLowerCase();
  const userEmail = String(context?.userEmail ?? '').trim().toLowerCase();
  if (
    tenantId === NEW_ASIA_TENANT_ID
    || tenantName.includes('new asia')
    || userEmail === 'admin@new-asia'
  ) {
    return 'new-asia';
  }
  return DEFAULT_MOCK_TENANT_KEY;
}

function findMockEmailRecord(emailId: string): EmailRecord | null {
  for (const records of Object.values(MOCK_EMAIL_RECORDS_BY_TENANT)) {
    const match = records.find((item) => item.id === emailId);
    if (match) return match;
  }
  return null;
}

function findMockWmsItem(predicate: (entry: MockWmsStateItem) => boolean): MockWmsStateItem | null {
  for (const state of Object.values(mockWmsStateByTenant)) {
    const match = state.find(predicate);
    if (match) return match;
  }
  return null;
}

function getMockEmailList(opts: {
  page: number;
  limit: number;
  search?: string;
  toAddr?: string;
  tenantId?: string | null;
  tenantName?: string | null;
  userEmail?: string | null;
}): PaginatedResponse<EmailRow> {
  const tenantKey = getMockTenantKey(opts);
  const filtered = (MOCK_EMAIL_RECORDS_BY_TENANT[tenantKey] ?? [])
    .filter((item) => {
      const toAddrs = normalizeToAddrs(item.to_addrs);
      const matchesToAddr = opts.toAddr
        ? toAddrs.some((addr) => addr.toLowerCase().includes(opts.toAddr!.toLowerCase()))
        : true;
      return matchesToAddr && matchesKeyword([item.subject, item.from_addr, item.body_text], opts.search ?? '');
    })
    .sort((left, right) => String(right.created_at).localeCompare(String(left.created_at)))
    .map((item) => ({
      id: item.id,
      from_addr: item.from_addr,
      subject: item.subject,
      direction: item.direction,
      created_at: item.created_at,
    }));
  return paginateItems(filtered, opts.page, opts.limit);
}

function getMockEmailRecord(emailId: string): EmailRecord | null {
  const record = findMockEmailRecord(emailId);
  return record ? cloneData(record) : null;
}

function getMockWmsInboxList(opts: {
  page: number;
  limit: number;
  keyword?: string;
  status?: string[];
  tenantId?: string | null;
  tenantName?: string | null;
  userEmail?: string | null;
}): PaginatedResponse<WmsInboxRowResponse> {
  const tenantKey = getMockTenantKey(opts);
  const filtered = (mockWmsStateByTenant[tenantKey] ?? [])
    .map((item) => item.row)
    .filter((row) => {
      const matchesStatus = !opts.status || opts.status.length === 0
        ? true
        : opts.status.includes(String(row.intakeStatus ?? 'received').toLowerCase());
      return matchesStatus && matchesKeyword([row.subject, row.from, row.bodyPreview, row.warehouseName], opts.keyword ?? '');
    })
    .sort((left, right) => String(right.receivedAt ?? '').localeCompare(String(left.receivedAt ?? '')));
  return paginateItems(cloneData(filtered), opts.page, opts.limit);
}

function getMockWmsDetail(intakeId: string): WmsIntakeDetail | null {
  const item = findMockWmsItem((entry) => entry.detail?.intakeId === intakeId);
  return item?.detail ? cloneData(item.detail) : null;
}

function createMockWmsDraft(messageId: string, payload: { directionHint: string; warehouseHint?: string }): { reused?: boolean; jobNumber?: string | null } {
  const item = findMockWmsItem((entry) => entry.row.messageId === messageId);
  if (!item) throw new Error('Mock intake email not found');
  if (item.row.intakeId && item.detail?.linkedJob?.jobNumber) {
    return { reused: true, jobNumber: item.detail.linkedJob.jobNumber ?? null };
  }

  const suffix = messageId.replace(/[^a-z0-9]/gi, '').slice(-4).toUpperCase() || '0001';
  const intakeId = `mock-intake-${suffix}`;
  const jobId = `job-mock-${suffix}`;
  const jobNumber = `WMS-MOCK-${suffix}`;
  const warehouseName = payload.warehouseHint?.trim() || item.row.warehouseName || 'Demo Warehouse';
  const direction = payload.directionHint || 'inbound';
  item.row.intakeId = intakeId;
  item.row.intakeStatus = 'parsed';
  item.row.warehouseName = warehouseName;
  item.row.linkedJobId = jobId;
  item.row.linkedJobNumber = jobNumber;
  item.row.linkedJobStatus = 'draft';
  item.detail = {
    intakeId,
    status: 'parsed',
    header: {
      jobType: direction === 'outbound' ? 'WAREHOUSE_OUT' : 'WAREHOUSE_IN',
      direction,
      requestDate: String(item.row.receivedAt ?? '').slice(0, 10),
      warehouseName,
      referenceNo: `REF-${suffix}`,
    },
    linkedJob: {
      id: jobId,
      jobNumber,
      jobType: direction === 'outbound' ? 'WAREHOUSE_OUT' : 'WAREHOUSE_IN',
      status: 'draft',
    },
    documents: item.row.hasAttachments
      ? [{
        id: `mock-doc-${suffix}`,
        attachmentId: `att-${suffix}`,
        docKind: 'EMAIL_ATTACHMENT',
        fileName: 'mock-intake-attachment.pdf',
        mimeType: 'application/pdf',
        fileSizeBytes: 128000,
        parseStatus: 'PARSED',
      }]
      : [],
    extractions: [
      { id: `ext-${suffix}-1`, field_name: 'warehouse_name', field_value: warehouseName, corrected_value: warehouseName, confidence_score: 0.88 },
      { id: `ext-${suffix}-2`, field_name: 'direction', field_value: direction, corrected_value: direction, confidence_score: 0.91 },
    ],
    email: {
      subject: item.row.subject ?? null,
      from: item.row.from ?? null,
      body: item.row.bodyPreview ?? null,
    },
  };
  return { reused: false, jobNumber };
}

function reviewMockWmsIntake(
  intakeId: string,
  payload: { decision: 'approve' | 'approve_with_changes' | 'reject'; job?: { direction?: string; warehouseName?: string; referenceNo?: string }; reviewNote?: string },
): void {
  const item = findMockWmsItem((entry) => entry.detail?.intakeId === intakeId);
  if (!item || !item.detail) throw new Error('Mock intake not found');
  const nextStatus = payload.decision === 'reject' ? 'rejected' : 'approved';
  item.row.intakeStatus = nextStatus;
  item.row.warehouseName = payload.job?.warehouseName?.trim() || item.row.warehouseName;
  item.row.linkedJobStatus = payload.decision === 'reject' ? 'draft' : 'pending';
  item.detail.status = nextStatus;
  item.detail.header = {
    ...item.detail.header,
    direction: payload.job?.direction?.trim() || item.detail.header?.direction || null,
    warehouseName: payload.job?.warehouseName?.trim() || item.detail.header?.warehouseName || null,
    referenceNo: payload.job?.referenceNo?.trim() || item.detail.header?.referenceNo || null,
  };
  if (item.detail.linkedJob) {
    item.detail.linkedJob.status = item.row.linkedJobStatus ?? item.detail.linkedJob.status ?? null;
  }
  if (payload.reviewNote?.trim()) {
    item.detail.extractions = [
      ...(item.detail.extractions ?? []),
      {
        id: `note-${Date.now()}`,
        field_name: 'review_note',
        field_value: payload.reviewNote.trim(),
        corrected_value: payload.reviewNote.trim(),
        confidence_score: 1,
      },
    ];
  }
}

function requestMockWmsApproval(intakeId: string, payload: { reviewerRole: string; comment?: string }): void {
  const item = findMockWmsItem((entry) => entry.detail?.intakeId === intakeId);
  if (!item || !item.detail) throw new Error('Mock intake not found');
  item.row.intakeStatus = 'review_required';
  item.detail.status = 'review_required';
  item.detail.extractions = [
    ...(item.detail.extractions ?? []),
    {
      id: `approval-${Date.now()}`,
      field_name: 'approval_request',
      field_value: payload.reviewerRole,
      corrected_value: payload.comment?.trim() || `Pending ${payload.reviewerRole}`,
      confidence_score: 1,
    },
  ];
}

function parseEmailAddr(raw: string): { name: string; email: string } {
  const match = raw.match(/^(.+?)\s*<(.+?)>$/);
  if (match && match[1] && match[2]) return { name: match[1].trim(), email: match[2].trim() };
  return { name: '', email: raw.trim() };
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-SG', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function normalizeToAddrs(val: unknown): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(String);
  if (typeof val === 'string') {
    if (val.startsWith('{') && val.endsWith('}')) {
      return val.slice(1, -1).split(',').map((item) => item.trim().replace(/^"|"$/g, ''));
    }
    return val.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatIntakeStatusLabel(status?: string | null): string {
  switch (status) {
    case 'received':
      return 'Received';
    case 'parsed':
      return 'Parsed';
    case 'review_required':
      return 'Review Required';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Not Started';
  }
}

function intakeStatusClass(status?: string | null): string {
  switch (status) {
    case 'approved':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'review_required':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'rejected':
      return 'border-rose-200 bg-rose-50 text-rose-700';
    case 'parsed':
      return 'border-blue-200 bg-blue-50 text-blue-700';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700';
  }
}

function buildInboxRow(row: EmailRow): InboxRow {
  return {
    id: row.id,
    source: 'email',
    emailId: row.id,
    fromAddr: row.fromAddr ?? row.from_addr ?? null,
    subject: row.subject ?? null,
    direction: row.direction ?? null,
    createdAt: row.createdAt ?? row.created_at ?? null,
  };
}

function buildWmsInboxRow(row: WmsInboxRowResponse): InboxRow {
  return {
    id: row.messageId,
    source: 'wms-intake',
    messageId: row.messageId,
    messageRowId: row.messageRowId ?? null,
    fromAddr: row.from ?? null,
    subject: row.subject ?? null,
    createdAt: row.receivedAt ?? null,
    hasAttachments: Boolean(row.hasAttachments),
    intakeId: row.intakeId ?? null,
    intakeStatus: row.intakeStatus ?? 'received',
    warehouseName: row.warehouseName ?? null,
    linkedJobId: row.linkedJobId ?? null,
    linkedJobNumber: row.linkedJobNumber ?? null,
    linkedJobStatus: row.linkedJobStatus ?? null,
    bodyPreview: row.bodyPreview ?? null,
  };
}

function parseStatusFilter(raw: string | null): string[] {
  return (raw ?? '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .filter((item) => item !== 'all');
}

function EmailDetailPanel({ emailId, mockMode }: { emailId: string; mockMode: boolean }) {
  const [record, setRecord] = useState<EmailRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showParsedData, setShowParsedData] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    if (mockMode) {
      const nextRecord = getMockEmailRecord(emailId);
      if (!cancelled) {
        setRecord(nextRecord);
        setError(nextRecord ? null : 'Mock email not found');
        setLoading(false);
      }
      return () => {
        cancelled = true;
      };
    }
    api.get<{ record: EmailRecord }>(`/entities/email-messages/${emailId}`)
      .then((res) => {
        if (!cancelled) setRecord(res.record ?? (res as unknown as EmailRecord));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [emailId, mockMode]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading...
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error ?? 'Email not found'}
        </div>
      </div>
    );
  }

  const from = parseEmailAddr(record.from_addr ?? '');
  const toAddrs = normalizeToAddrs(record.to_addrs);
  const isInbound = record.direction === 'inbound';
  const attachments = record.attachments ?? [];
  const hasParsedData = record.parsed_data && Object.keys(record.parsed_data).length > 0;

  return (
    <ScrollArea className="h-full">
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          {isInbound
            ? <Mail className="h-5 w-5 shrink-0 text-blue-500" />
            : <MailOpen className="h-5 w-5 shrink-0 text-green-500" />}
          <Badge variant={isInbound ? 'default' : 'secondary'} className="shrink-0">
            {isInbound ? 'Received' : 'Sent'}
          </Badge>
        </div>

        <Card>
          <div className="px-6 pb-3 pt-5">
            <h1 className="text-lg font-semibold leading-snug">
              {record.subject || '(No Subject)'}
            </h1>
          </div>
          <Separator />
          <div className="space-y-3 bg-muted/30 px-6 py-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {(from.name || from.email).charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{from.name || from.email}</span>
                  {from.name ? <span className="text-xs text-muted-foreground">&lt;{from.email}&gt;</span> : null}
                </div>
                {toAddrs.length > 0 ? (
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users className="h-3 w-3 shrink-0" />
                    <span>To: {toAddrs.join(', ')}</span>
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {formatDate(record.created_at)}
              </div>
            </div>
            {hasParsedData && typeof record.parsed_data?.intent === 'string' ? (
              <div className="flex items-center gap-2">
                <Tag className="h-3 w-3 text-muted-foreground" />
                <Badge variant="outline" className="text-xs capitalize">
                  {String(record.parsed_data?.intent ?? '').replace(/_/g, ' ')}
                </Badge>
                {record.job_id ? (
                  <Link to={`/e/jobs/${record.job_id}`}>
                    <Badge variant="outline" className="cursor-pointer text-xs hover:bg-muted">
                      <ExternalLink className="mr-1 h-3 w-3" /> Linked Job
                    </Badge>
                  </Link>
                ) : null}
              </div>
            ) : null}
          </div>
          <Separator />
          <CardContent className="pt-5">
            {record.body_text ? (
              <div className="whitespace-pre-wrap font-[system-ui] text-sm leading-relaxed text-foreground">
                {record.body_text}
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground">No message body</p>
            )}
          </CardContent>
          <Separator />
          <div className="px-6 py-4">
            {attachments.length > 0 ? (
              <>
                <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Paperclip className="h-3 w-3" /> Attachments ({attachments.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {attachments.map((att, index) => (
                    <a
                      key={`${att.name ?? 'attachment'}-${index}`}
                      href={att.url ?? '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs transition-colors hover:bg-muted"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="max-w-[200px] truncate font-medium">{att.name}</span>
                      {att.size != null ? <span className="text-muted-foreground">({formatFileSize(att.size)})</span> : null}
                    </a>
                  ))}
                </div>
              </>
            ) : (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Paperclip className="h-3 w-3" /> No attachments
              </p>
            )}
          </div>
        </Card>

        {hasParsedData ? (
          <Card>
            <button
              className="flex w-full items-center justify-between rounded-xl px-6 py-3 text-left transition-colors hover:bg-muted/50"
              onClick={() => setShowParsedData(!showParsedData)}
              type="button"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <Braces className="h-4 w-4 text-muted-foreground" /> AI Parsed Data
              </span>
              {showParsedData
                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {showParsedData ? (
              <>
                <Separator />
                <CardContent className="pt-4">
                  <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-muted/50 p-4 text-xs leading-relaxed">
                    {JSON.stringify(record.parsed_data, null, 2)}
                  </pre>
                </CardContent>
              </>
            ) : null}
          </Card>
        ) : null}
      </div>
    </ScrollArea>
  );
}

function WmsIntakeDetailPanel({
  row,
  onRefresh,
  mockMode,
}: {
  row: InboxRow;
  onRefresh: () => Promise<void>;
  mockMode: boolean;
}) {
  const [detail, setDetail] = useState<WmsIntakeDetail | null>(null);
  const [loading, setLoading] = useState(Boolean(row.intakeId));
  const [error, setError] = useState<string | null>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [showExtractions, setShowExtractions] = useState(false);
  const [warehouseHint, setWarehouseHint] = useState('');
  const [directionHint, setDirectionHint] = useState<'inbound' | 'outbound' | 'export_to_port' | 'internal'>('inbound');
  const [reviewDirection, setReviewDirection] = useState('');
  const [reviewWarehouse, setReviewWarehouse] = useState('');
  const [reviewReference, setReviewReference] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [reviewerRole, setReviewerRole] = useState(DEFAULT_REVIEWER_ROLE);

  const refreshDetail = useCallback(async () => {
    if (!row.intakeId) {
      setDetail(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const nextDetail = mockMode
        ? getMockWmsDetail(row.intakeId)
        : await api.get<WmsIntakeDetail>(`/wms/intake/${row.intakeId}`);
      if (!nextDetail) throw new Error('Mock WMS intake detail not found');
      setDetail(nextDetail);
      setReviewDirection(nextDetail.header?.direction ?? '');
      setReviewWarehouse(nextDetail.header?.warehouseName ?? '');
      setReviewReference(nextDetail.header?.referenceNo ?? '');
    } catch (err) {
      setDetail(null);
      setError(err instanceof Error ? err.message : 'Failed to load WMS intake detail');
    } finally {
      setLoading(false);
    }
  }, [mockMode, row.intakeId]);

  useEffect(() => {
    setReviewNote('');
    setReviewerRole(DEFAULT_REVIEWER_ROLE);
    void refreshDetail();
  }, [refreshDetail]);

  const handleCreateDraft = async () => {
    if (!row.messageId) return;
    setCreatingDraft(true);
    try {
      const payload = {
        directionHint,
        warehouseHint: warehouseHint.trim() || undefined,
      };
      const result = mockMode
        ? createMockWmsDraft(row.messageId, payload)
        : await api.post<{ reused?: boolean; jobNumber?: string | null }>(
          `/wms/intake/inbox/${encodeURIComponent(row.messageId)}/create-draft`,
          payload,
        );
      toast.success(result.reused ? 'Reused existing WMS draft job' : 'WMS draft job created');
      await onRefresh();
      await refreshDetail();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create WMS draft');
    } finally {
      setCreatingDraft(false);
    }
  };

  const handleReview = async (decision: 'approve' | 'approve_with_changes' | 'reject') => {
    if (!row.intakeId) return;
    setReviewing(true);
    try {
      const payload = {
        decision,
        job: {
          direction: reviewDirection.trim() || undefined,
          warehouseName: reviewWarehouse.trim() || undefined,
          referenceNo: reviewReference.trim() || undefined,
        },
        reviewNote: reviewNote.trim() || undefined,
      };
      if (mockMode) reviewMockWmsIntake(row.intakeId, payload);
      else await api.post(`/wms/intake/${row.intakeId}/review`, payload);
      toast.success(decision === 'reject' ? 'WMS intake rejected' : 'WMS intake reviewed');
      await onRefresh();
      await refreshDetail();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to review WMS intake');
    } finally {
      setReviewing(false);
    }
  };

  const handleRequestApproval = async () => {
    if (!row.intakeId) return;
    const role = reviewerRole.trim();
    if (!role) {
      toast.error('Reviewer role is required');
      return;
    }
    setRequestingApproval(true);
    try {
      const payload = {
        reviewerRole: role,
        approvalType: 'wms_job_intake_confirm',
        comment: reviewNote.trim() || undefined,
      };
      if (mockMode) requestMockWmsApproval(row.intakeId, payload);
      else await api.post(`/wms/intake/${row.intakeId}/request-approval`, payload);
      toast.success('Approval request submitted');
      await refreshDetail();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to request approval');
    } finally {
      setRequestingApproval(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading WMS intake...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error}
        </div>
      </div>
    );
  }

  const effectiveStatus = detail?.status ?? row.intakeStatus ?? 'received';
  const documents = detail?.documents ?? [];
  const extractions = detail?.extractions ?? [];
  const summaryItems = [
    { label: 'Intake ID', value: row.intakeId ?? row.messageId ?? 'Pending creation' },
    { label: 'Direction', value: detail?.header?.direction ?? 'Pending review' },
    { label: 'Warehouse', value: detail?.header?.warehouseName ?? row.warehouseName ?? 'Not set' },
    { label: 'Reference No', value: detail?.header?.referenceNo ?? 'Not set' },
  ];

  return (
    <ScrollArea className="h-full">
      <div className="space-y-4 p-4">
        <Card>
          <div className="space-y-4 px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={cn('text-xs', intakeStatusClass(effectiveStatus))}>
                    {formatIntakeStatusLabel(effectiveStatus)}
                  </Badge>
                  {row.hasAttachments ? (
                    <Badge variant="outline" className="text-xs">
                      <Paperclip className="mr-1 h-3 w-3" /> Attachments
                    </Badge>
                  ) : null}
                </div>
                <h1 className="text-lg font-semibold leading-snug">
                  {detail?.email?.subject ?? row.subject ?? '(No Subject)'}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" />
                    {detail?.email?.from ?? row.fromAddr ?? 'Unknown sender'}
                  </span>
                  {row.createdAt ? (
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {formatDate(row.createdAt)}
                    </span>
                  ) : null}
                  {detail?.linkedJob?.status ? (
                    <Badge variant="outline" className="text-[10px]">
                      Job {detail.linkedJob.status}
                    </Badge>
                  ) : null}
                </div>
              </div>
              {detail?.linkedJob ? (
                <Link to={`/e/jobs/${detail.linkedJob.id}`}>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <ExternalLink className="h-3.5 w-3.5" />
                    {detail.linkedJob.jobNumber ?? 'Open linked job'}
                  </Button>
                </Link>
              ) : null}
            </div>

            <Separator />

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {summaryItems.map((item) => (
                <div key={item.label} className="rounded-lg border bg-muted/20 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{item.label}</div>
                  <div className="mt-1 break-words text-sm font-medium">{item.value}</div>
                </div>
              ))}
            </div>

            <Separator />

            {detail?.email?.body || row.bodyPreview ? (
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {detail?.email?.body ?? row.bodyPreview}
              </div>
            ) : (
              <div className="text-sm italic text-muted-foreground">No email body preview available yet.</div>
            )}
          </div>
        </Card>

        {!row.intakeId ? (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkles className="h-4 w-4 text-sky-600" />
                Create WMS Draft From Existing Inbox Message
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Direction Hint</p>
                  <div className="flex flex-wrap gap-2">
                    {(['inbound', 'outbound', 'export_to_port', 'internal'] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={cn(
                          'rounded-full border px-3 py-1 text-xs transition-colors',
                          directionHint === option
                            ? 'border-sky-300 bg-sky-50 text-sky-700'
                            : 'border-border bg-background text-muted-foreground hover:bg-muted',
                        )}
                        onClick={() => setDirectionHint(option)}
                      >
                        {option.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Warehouse Hint</p>
                  <Input
                    placeholder="Optional warehouse name"
                    value={warehouseHint}
                    onChange={(event) => setWarehouseHint(event.target.value)}
                  />
                </div>
              </div>
              <Button onClick={() => void handleCreateDraft()} disabled={creatingDraft} className="gap-1.5">
                {creatingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Create Draft Job
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ShieldCheck className="h-4 w-4 text-violet-600" />
                  Intake Review Actions
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Direction</p>
                    <Input
                      placeholder="Direction"
                      value={reviewDirection}
                      onChange={(event) => setReviewDirection(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Warehouse</p>
                    <Input
                      placeholder="Warehouse"
                      value={reviewWarehouse}
                      onChange={(event) => setReviewWarehouse(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Reference No</p>
                    <Input
                      placeholder="Reference number"
                      value={reviewReference}
                      onChange={(event) => setReviewReference(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Reviewer Role</p>
                    <Input
                      placeholder="warehouse_supervisor"
                      value={reviewerRole}
                      onChange={(event) => setReviewerRole(event.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Review Note</p>
                  <Textarea
                    rows={4}
                    placeholder="Add intake review note or approval comment"
                    value={reviewNote}
                    onChange={(event) => setReviewNote(event.target.value)}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => void handleReview('approve')} disabled={reviewing}>
                    {reviewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Approve
                  </Button>
                  <Button variant="secondary" onClick={() => void handleReview('approve_with_changes')} disabled={reviewing}>
                    Approve With Changes
                  </Button>
                  <Button variant="outline" onClick={() => void handleRequestApproval()} disabled={requestingApproval}>
                    {requestingApproval ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Request Approval
                  </Button>
                  <Button variant="destructive" onClick={() => void handleReview('reject')} disabled={reviewing}>
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 pt-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium">Supporting Documents</div>
                  <Badge variant="outline">{documents.length} doc(s)</Badge>
                </div>
                {documents.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No intake documents attached yet.</div>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.id} className="rounded-lg border px-3 py-2 text-sm">
                        <div className="font-medium">{doc.fileName ?? doc.docKind ?? 'Untitled document'}</div>
                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span>{doc.docKind ?? 'UNKNOWN'}</span>
                          <span>{doc.parseStatus ?? 'PENDING'}</span>
                          {typeof doc.fileSizeBytes === 'number' ? <span>{formatFileSize(doc.fileSizeBytes)}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {extractions.length > 0 ? (
              <Card>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-6 py-4 text-left"
                  onClick={() => setShowExtractions((current) => !current)}
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Braces className="h-4 w-4 text-muted-foreground" />
                    AI Extractions
                  </span>
                  {showExtractions
                    ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </button>
                {showExtractions ? (
                  <>
                    <Separator />
                    <CardContent className="space-y-2 pt-4">
                      {extractions.map((item) => (
                        <div key={item.id} className="rounded-lg border px-3 py-2 text-sm">
                          <div className="font-medium">{item.field_name ?? 'field'}</div>
                          <div className="mt-1 break-words text-muted-foreground">
                            {String(item.corrected_value ?? item.field_value ?? '—')}
                          </div>
                          {typeof item.confidence_score === 'number' ? (
                            <div className="mt-1 text-xs text-muted-foreground">
                              Confidence {Math.round(item.confidence_score * 100)}%
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </CardContent>
                  </>
                ) : null}
              </Card>
            ) : null}
          </>
        )}
      </div>
    </ScrollArea>
  );
}

export function EmailInboxWorkspace({
  mode: forcedMode,
  embedded = false,
  defaultWmsStatusFilter = EMPTY_WMS_STATUS_FILTER,
}: EmailInboxWorkspaceProps) {
  const { user, tenants } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [forcedStatusFilter, setForcedStatusFilter] = useState<string[]>(defaultWmsStatusFilter);
  const liveParam = searchParams.get('live');
  const sourceParam = searchParams.get('source');
  const toAddrParam = searchParams.get('toAddr') ?? '';
  const statusParam = searchParams.get('status');
  const mockMode = DEFAULT_MOCK_INBOX_MODE && liveParam !== '1';

  const source: InboxSource = forcedMode ?? (sourceParam === 'wms-intake' ? 'wms-intake' : 'email');
  const toAddrFilter = forcedMode ? '' : toAddrParam;
  const wmsStatusFilter = useMemo(
    () => {
      if (forcedMode) return forcedStatusFilter;
      if (source !== 'wms-intake') return [];
      return parseStatusFilter(statusParam);
    },
    [forcedMode, forcedStatusFilter, source, statusParam],
  );
  const shouldAutoselectFirstRow = embedded || source === 'wms-intake';

  useEffect(() => {
    setForcedStatusFilter(defaultWmsStatusFilter);
  }, [defaultWmsStatusFilter]);

  useEffect(() => {
    setPage(1);
    setSelectedId(null);
  }, [source, toAddrFilter, wmsStatusFilter.join(',')]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const currentTenantName = useMemo(
    () => tenants.find((tenant) => tenant.id === user?.tenantId)?.name ?? null,
    [tenants, user?.tenantId],
  );

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (source === 'wms-intake') {
        const res = mockMode
          ? getMockWmsInboxList({
            page,
            limit: PAGE_SIZE,
            keyword: search || undefined,
            status: wmsStatusFilter,
            tenantId: user?.tenantId,
            tenantName: currentTenantName,
            userEmail: user?.email,
          })
          : await api.get<PaginatedResponse<WmsInboxRowResponse>>('/wms/intake/inbox', {
            page,
            limit: PAGE_SIZE,
            keyword: search || undefined,
            status: wmsStatusFilter.length > 0 ? wmsStatusFilter.join(',') : undefined,
          });
        const nextRows = (res.items ?? []).map(buildWmsInboxRow);
        setRows(nextRows);
        setTotal(res.total ?? 0);
        setSelectedId((current) => current && nextRows.some((item) => item.id === current)
          ? current
          : (shouldAutoselectFirstRow ? nextRows[0]?.id ?? null : current));
        return;
      }

      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (search) params.set('search', search);
      if (toAddrFilter) params.set('toAddr', toAddrFilter);
      const res = mockMode
        ? getMockEmailList({
          page,
          limit: PAGE_SIZE,
          search: search || undefined,
          toAddr: toAddrFilter || undefined,
          tenantId: user?.tenantId,
          tenantName: currentTenantName,
          userEmail: user?.email,
        })
        : await api.get<PaginatedResponse<EmailRow>>(`/entities/email-messages?${params.toString()}`);
      const nextRows = (res.items ?? []).map(buildInboxRow);
      setRows(nextRows);
      setTotal(res.total ?? 0);
      setSelectedId((current) => current && nextRows.some((item) => item.id === current)
        ? current
        : (shouldAutoselectFirstRow ? nextRows[0]?.id ?? null : current));
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [currentTenantName, mockMode, page, search, shouldAutoselectFirstRow, source, toAddrFilter, user?.email, user?.tenantId, wmsStatusFilter]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const title = useMemo(() => {
    const label = source === 'wms-intake' ? 'WMS Intake Inbox' : 'Email';
    return `${label}${total ? ` (${total.toLocaleString()})` : ''}`;
  }, [source, total]);

  const selectedRow = rows.find((row) => row.id === selectedId) ?? null;

  const switchSource = (nextSource: InboxSource) => {
    if (forcedMode) return;
    const nextParams = new URLSearchParams(searchParams);
    if (nextSource === 'wms-intake') {
      nextParams.set('source', 'wms-intake');
      nextParams.delete('toAddr');
    } else {
      nextParams.delete('source');
      nextParams.delete('status');
    }
    setSearchParams(nextParams);
  };

  const toggleWmsStatus = (status: string) => {
    const current = new Set(wmsStatusFilter);
    const nextStatusFilter = status === 'all'
      ? []
      : (current.has(status)
        ? wmsStatusFilter.filter((item) => item !== status)
        : [...wmsStatusFilter, status]);

    if (forcedMode) {
      setForcedStatusFilter(nextStatusFilter);
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    if (nextStatusFilter.length === 0) nextParams.delete('status');
    else nextParams.set('status', nextStatusFilter.join(','));
    setSearchParams(nextParams);
  };

  return (
    <div className={cn(
      'flex overflow-hidden bg-background',
      embedded ? 'h-[720px] rounded-2xl border shadow-sm' : 'h-[calc(100vh-64px)]',
    )}>
      <div className={cn(
        'flex flex-col border-r bg-background',
        selectedId ? 'w-[380px] shrink-0' : 'flex-1 max-w-2xl',
      )}>
        <div className="shrink-0 space-y-2 border-b px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-base font-semibold">
              <Mail className="h-5 w-5 text-sky-600" />
              {title}
              {mockMode ? <Badge variant="secondary" className="text-[10px] uppercase">Mock</Badge> : null}
            </div>
            {!forcedMode ? (
              <div className="inline-flex rounded-lg border bg-muted/40 p-1 text-xs">
                <button
                  type="button"
                  className={cn(
                    'rounded-md px-2.5 py-1 transition-colors',
                    source === 'email' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
                  )}
                  onClick={() => switchSource('email')}
                >
                  All Email
                </button>
                <button
                  type="button"
                  className={cn(
                    'rounded-md px-2.5 py-1 transition-colors',
                    source === 'wms-intake' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground',
                  )}
                  onClick={() => switchSource('wms-intake')}
                >
                  WMS Intake
                </button>
              </div>
            ) : null}
          </div>

          {toAddrFilter ? (
            <div className="flex items-center gap-2 rounded-lg bg-sky-50 px-3 py-1.5 text-sm dark:bg-sky-900/20">
              <Mail className="h-3.5 w-3.5 shrink-0 text-sky-600" />
              <span className="flex-1 truncate font-mono text-xs text-sky-800 dark:text-sky-300">{toAddrFilter}</span>
              <button
                type="button"
                onClick={() => setSearchParams({})}
                className="rounded p-0.5 text-sky-600 hover:bg-sky-100 dark:hover:bg-sky-800"
                title="Clear filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}

          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={source === 'wms-intake' ? 'Search intake subject / sender' : 'Search subject / sender'}
              className="h-9 pl-9 text-sm"
            />
          </div>

          {source === 'wms-intake' ? (
            <div className="flex flex-wrap gap-2">
              {WMS_STATUS_OPTIONS.map((option) => {
                const active = option.value === 'all'
                  ? wmsStatusFilter.length === 0
                  : wmsStatusFilter.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleWmsStatus(option.value)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-[11px] transition-colors',
                      active
                        ? 'border-sky-300 bg-sky-50 text-sky-700'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex min-h-[180px] items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {source === 'wms-intake' ? 'Loading WMS intake...' : 'Loading email...'}
            </div>
          ) : error ? (
            <div className="m-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="m-3 rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
              {source === 'wms-intake' ? 'No WMS intake email found.' : 'No email found.'}
            </div>
          ) : (
            <div className="divide-y">
              {rows.map((row) => {
                const isActive = row.id === selectedId;
                return (
                  <button
                    key={row.id}
                    type="button"
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                      isActive
                        ? 'border-l-2 border-l-sky-500 bg-sky-50 dark:bg-sky-900/20'
                        : 'hover:bg-muted/40',
                    )}
                    onClick={() => setSelectedId(row.id)}
                  >
                    <div className="mt-0.5 rounded-lg bg-sky-50 p-1.5 text-sky-700">
                      <Mail className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-medium">{row.subject || 'No subject'}</p>
                        {row.direction ? <Badge variant="outline" className="shrink-0 text-[10px]">{row.direction}</Badge> : null}
                        {row.source === 'wms-intake' ? (
                          <Badge variant="outline" className={cn('shrink-0 text-[10px]', intakeStatusClass(row.intakeStatus))}>
                            {formatIntakeStatusLabel(row.intakeStatus)}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">{row.fromAddr || 'Unknown sender'}</p>
                      {row.source === 'wms-intake' && row.bodyPreview ? (
                        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                          {row.bodyPreview}
                        </p>
                      ) : null}
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{row.createdAt ? new Date(row.createdAt).toLocaleString() : 'Unknown date'}</span>
                        {row.source === 'wms-intake' && row.hasAttachments ? (
                          <span className="inline-flex items-center gap-1">
                            <Paperclip className="h-3 w-3" />
                            attachments
                          </span>
                        ) : null}
                        {row.source === 'wms-intake' && row.warehouseName ? (
                          <span className="inline-flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            {row.warehouseName}
                          </span>
                        ) : null}
                        {row.source === 'wms-intake' && row.linkedJobNumber ? (
                          <span className="inline-flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" />
                            {row.linkedJobNumber}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {!loading && total > PAGE_SIZE ? (
          <div className="flex shrink-0 items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
            <span>
              {((page - 1) * PAGE_SIZE + 1).toLocaleString()}-{Math.min(page * PAGE_SIZE, total).toLocaleString()} / {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                <ChevronLeft className="h-3 w-3" />
              </Button>
              <span className="px-1.5 font-mono">{page}/{totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="min-w-0 flex-1">
        {selectedRow ? (
          selectedRow.source === 'wms-intake' ? (
            <WmsIntakeDetailPanel key={selectedRow.id} row={selectedRow} onRefresh={loadRows} mockMode={mockMode} />
          ) : (
            <EmailDetailPanel
              key={selectedRow.emailId ?? selectedRow.id}
              emailId={selectedRow.emailId ?? selectedRow.id}
              mockMode={mockMode}
            />
          )
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <div className="space-y-2 text-center">
              <Mail className="mx-auto h-10 w-10 opacity-30" />
              <p className="text-sm">
                {source === 'wms-intake' ? 'Select an intake email to review' : 'Select an email to read'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EmailMessagesInboxPage() {
  return <EmailInboxWorkspace />;
}
